// Header.jsx
import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, MessageSquare } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import "./Header.css";

const links = [
  { to: "/events", label: "Events" },
  { to: "/lobby", label: "The Lobby" },
  { to: "/network", label: "Members" },
  { to: "/host", label: "Host" },
  { to: "/sponsor", label: "Sponsor" },
  { to: "/user-meet", label: "User Meet" },
];

export default function Header() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const nav = useNavigate();

  useEffect(() => {
    if (!user) {
      setUnread(0);
      return;
    }
    const timer = setTimeout(() => {
      setUnread(3);
    }, 600);
    return () => clearTimeout(timer);
  }, [user]);

  return (
    <header
      data-testid="site-header"
      className="siteHeader"
      style={{
        background: "rgba(6,20,18,0.72)",
        backdropFilter: "blur(20px)",
      }}
    >
      <div className="container-x headerInner">
        {/* Brand */}
        <Link to="/" data-testid="brand-link" className="headerBrand">
          <div className="headerLogoMark" />
          <span className="headerLogoText">vmeetio</span>
          <span className="overline headerEst">est. 2026</span>
        </Link>

        {/* Desktop nav */}
        <nav className="headerDesktopNav">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(" ", "-")}`}
              className={({ isActive }) =>
                `headerNavLink ${isActive ? "headerNavLinkActive" : ""}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        {/* Desktop actions */}
        <div className="headerDesktopActions">
          {user && typeof user === "object" ? (
            <>
              <Link
                data-testid="header-my-events-link"
                to="/my-events"
                className="headerActionLink"
              >
                My events
              </Link>
              <Link
                data-testid="header-messages-link"
                to="/messages"
                className="headerMessagesLink"
              >
                <MessageSquare size={14} /> Messages
                {unread > 0 && (
                  <span
                    data-testid="header-unread-count"
                    className="headerUnreadBadge"
                  >
                    {unread}
                  </span>
                )}
              </Link>
              {user.role === "admin" && (
                <Link
                  data-testid="header-admin-link"
                  to="/admin"
                  className="headerAdminLink"
                >
                  Admin
                </Link>
              )}
              <span
                className="headerUserEmail"
                data-testid="header-user-email"
              >
                {user.email}
              </span>
              <button
                data-testid="logout-btn"
                onClick={async () => {
                  await logout();
                  nav("/");
                }}
                className="btn-ghost headerSignOutBtn"
              >
                Sign out
              </button>
            </>
          ) : (
            <>
              <Link
                data-testid="header-login-link"
                to="/login"
                className="headerActionLink"
              >
                Sign in
              </Link>
              <Link
                data-testid="header-join-link"
                to="/register"
                className="btn-gold headerJoinBtn"
              >
                Request access
              </Link>
            </>
          )}
        </div>

        {/* Mobile toggle */}
        <button
          data-testid="mobile-menu-toggle"
          className="headerMobileToggle"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="headerMobileDrawer">
          <div className="container-x headerMobileInner">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className="headerMobileNavLink"
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(" ", "-")}`}
              >
                {l.label}
              </Link>
            ))}

            {user && typeof user === "object" ? (
              <>
                <Link
                  data-testid="mobile-my-events-link"
                  to="/my-events"
                  onClick={() => setOpen(false)}
                  className="headerMobileAccentLink"
                >
                  My events
                </Link>
                <Link
                  data-testid="mobile-messages-link"
                  to="/messages"
                  onClick={() => setOpen(false)}
                  className="headerMobileAccentLink headerMobileMessagesLink"
                >
                  Messages
                  {unread > 0 && (
                    <span className="headerUnreadBadge">{unread}</span>
                  )}
                </Link>
                {user.role === "admin" && (
                  <Link
                    data-testid="mobile-admin-link"
                    to="/admin"
                    onClick={() => setOpen(false)}
                    className="headerMobileAdminLink"
                  >
                    Admin
                  </Link>
                )}
                <button
                  data-testid="mobile-logout-btn"
                  onClick={async () => {
                    await logout();
                    setOpen(false);
                    nav("/");
                  }}
                  className="btn-ghost headerMobileSignOutBtn"
                >
                  Sign out
                </button>
              </>
            ) : (
              <div className="headerMobileAuthRow">
                <Link
                  onClick={() => setOpen(false)}
                  to="/login"
                  className="btn-ghost headerMobileAuthBtn"
                >
                  Sign in
                </Link>
                <Link
                  onClick={() => setOpen(false)}
                  to="/register"
                  className="btn-gold headerMobileAuthBtn"
                >
                  Join
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}